/**
 * Infrastructure tab — cloud backend configuration (the InfrastructureEditor
 * over the shared config draft) plus the lifecycle actions that operate on
 * provisioned infrastructure (rotate startup scripts, refresh, reset).
 */

import { InfrastructureEditor } from '../profiles/InfrastructureEditor';
import styles from '../../routes/admin/pools/PoolDetail.module.css';
import type { PoolConfigDraftApi } from './usePoolConfigDraft';

export interface InfrastructureTabProps {
  draftApi: PoolConfigDraftApi;
  saving: boolean;
  infraDefaults: unknown;
  actionInProgress: string | null;
  provisionResult: { ok: boolean; message: string } | null;
  onRotateScripts: () => void;
  onReprovision: (reset: boolean) => void;
}

export function InfrastructureTab({
  draftApi,
  saving,
  infraDefaults,
  actionInProgress,
  provisionResult,
  onRotateScripts,
  onReprovision,
}: InfrastructureTabProps) {
  const { draft, patch } = draftApi;
  if (!draft) return <div className={styles.loading}>Loading configuration…</div>;

  return (
    <>
      {/* Backend configuration */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Backends</h2>
        </div>
        <div className={styles.formHelp}>
          Changes here are part of the pool configuration draft — Save applies them, then
          use Refresh Infrastructure below to rebuild instances on the new definition.
        </div>
        <InfrastructureEditor
          value={draft.infrastructure}
          onChange={infrastructure => patch({ infrastructure })}
          disabled={saving}
          infraDefaults={infraDefaults}
        />
      </div>

      {/* Lifecycle actions */}
      <div className={styles.section}>
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>Provisioned Infrastructure</h2>
        </div>
        <div className={styles.controlsGrid}>
          <div className={styles.controlCard}>
            <div className={styles.controlCardTitle}>Rotate Startup Scripts</div>
            <p style={{ fontSize: 'var(--text-sm)', opacity: 0.7, marginTop: 0, marginBottom: 'var(--space-2)' }}>
              Update launch templates with the latest startup scripts. Only new instances are affected.
            </p>
            <button
              className={styles.actionButton}
              onClick={onRotateScripts}
              disabled={!!actionInProgress}
            >
              {actionInProgress === 'rotate-scripts' ? 'Rotating...' : 'Rotate Scripts'}
            </button>
          </div>

          <div className={styles.controlCard}>
            <div className={styles.controlCardTitle}>Rebuild</div>
            <p style={{ fontSize: 'var(--text-sm)', opacity: 0.7, marginTop: 0, marginBottom: 'var(--space-2)' }}>
              Teardown and rebuild infrastructure. Terminates all running instances.
            </p>
            <div className={styles.controlActions}>
              <button
                className={styles.applyButton}
                onClick={() => onReprovision(false)}
                disabled={!!actionInProgress}
              >
                {actionInProgress === 'provision' ? 'Working...' : 'Refresh Infrastructure'}
              </button>
              <button
                className={styles.dangerButton}
                onClick={() => onReprovision(true)}
                disabled={!!actionInProgress}
              >
                Reset Infrastructure
              </button>
            </div>
            <p style={{ fontSize: 'var(--text-xs)', opacity: 0.5, marginTop: 'var(--space-1)', marginBottom: 0 }}>
              Refresh preserves manual scale overrides. Reset clears all overrides to defaults.
            </p>
          </div>
        </div>
        {provisionResult && (
          <div style={{
            marginTop: 'var(--space-2)',
            padding: 'var(--space-2) var(--space-3)',
            fontSize: 'var(--text-xs)',
            border: 'var(--border-width) solid',
            borderColor: provisionResult.ok ? 'var(--text-primary)' : 'var(--color-error)',
            color: provisionResult.ok ? 'var(--text-primary)' : 'var(--color-error)',
          }}>
            {provisionResult.message}
          </div>
        )}
      </div>
    </>
  );
}
